import {
  WIDGET_COOKIE_NAME,
  type WidgetCacheEntry,
  type WidgetUser,
} from '@/config';

const isWidgetUser = (value: any): value is WidgetUser =>
  !!value &&
  typeof value.username === 'string' &&
  typeof value.externalId === 'string';

const isWidgetCacheEntry = (value: any): value is WidgetCacheEntry =>
  !!value && typeof value.appId === 'string' && isWidgetUser(value.user);

/**
 * Reads the whole ref-keyed widget cache. Any value that does not match the
 * current shape is dropped, which includes the pre-multi-app flat
 * `{ username, externalId }` cookie: it cannot be migrated, because whether the
 * current ref resolves to the app that user belongs to is only knowable from a
 * server response. The cookie is session-scoped, so those users simply
 * re-register once.
 */
export const readWidgetCache = (): Record<string, WidgetCacheEntry> => {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${WIDGET_COOKIE_NAME}=`));

  if (!match) return {};

  try {
    const parsed = JSON.parse(
      decodeURIComponent(match.split('=').slice(1).join('=')),
    );

    if (!parsed || typeof parsed !== 'object') return {};

    return Object.entries(parsed).reduce<Record<string, WidgetCacheEntry>>(
      (acc, [ref, entry]) => {
        if (isWidgetCacheEntry(entry)) {
          acc[ref] = entry;
        }

        return acc;
      },
      {},
    );
  } catch {
    return {};
  }
};

export default function getWidgetCookie(ref: string): WidgetCacheEntry | null {
  return readWidgetCache()[ref] || null;
}
